import { type RefObject } from 'react';

import { COPILOT } from '@cast/constants';
import {
  Divider,
  IconButton,
  Icons,
  Typography,
  Stack,
  tokens,
} from '@cast/design-system';

type ChatHeaderProps = {
  onToggleHistory: () => void;
  onStartNewChat: () => void;
  onClose: () => void;
  showHistory: boolean;
  showFullScreenButton: boolean;
  isFullScreen: boolean;
  onToggleFullScreen: () => void;
  historyBtnRef?: RefObject<HTMLButtonElement | null>;
};

export const ChatHeader = ({
  onToggleHistory,
  onStartNewChat,
  onClose,
  showHistory,
  showFullScreenButton,
  isFullScreen,
  onToggleFullScreen,
  historyBtnRef,
}: ChatHeaderProps) => {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
        backgroundColor: tokens.theme.bg.structural.surface,
        px: 24,
        py: 16,
        borderBottom: '1px solid',
        borderBottomColor: tokens.theme.border.structural.neutral,
      }}
    >
      <Stack direction="row" alignItems="center" gap="8px">
        <Icons.CastCopilot size={20} color={tokens.theme.fg.action.primary} />
        <Typography variant="h6" color={tokens.theme.fg.structural.primary}>
          {COPILOT}
        </Typography>
      </Stack>
      <Stack direction="row" gap={8} alignItems="center">
        {!isFullScreen && (
          <IconButton
            ref={historyBtnRef}
            type="button"
            variant="ghost"
            size="sm"
            onClick={onToggleHistory}
            title="Chat history"
            aria-label="Chat history"
            aria-pressed={showHistory}
            sx={
              showHistory
                ? {
                    backgroundColor: tokens.theme.bg.structural.surfaceRaised,
                  }
                : undefined
            }
            data-testid="chat-history-toggle"
          >
            <Icons.ChatsTeardrop
              size={20}
              weight={showHistory ? 'fill' : 'regular'}
            />
          </IconButton>
        )}

        <IconButton
          type="button"
          variant="ghost"
          size="sm"
          onClick={onStartNewChat}
          title="Start new chat"
          aria-label="Start new chat"
          data-testid="chat-new-chat-button"
        >
          <Icons.NotePencil size={20} />
        </IconButton>
        {showFullScreenButton ? (
          <IconButton
            type="button"
            variant="ghost"
            size="sm"
            title="Toggle fullscreen mode"
            aria-label={isFullScreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            onClick={onToggleFullScreen}
            data-testid="chat-fullscreen-toggle"
          >
            {isFullScreen ? (
              <Icons.ArrowsIn size={20} />
            ) : (
              <Icons.ArrowsOut size={20} />
            )}
          </IconButton>
        ) : null}

        <Divider orientation="vertical" sx={{ height: 20 }} />

        <IconButton
          type="button"
          variant="ghost"
          size="sm"
          onClick={onClose}
          title="Close chat"
          data-testid="chat-close-button"
        >
          <Icons.X size={20} />
        </IconButton>
      </Stack>
    </Stack>
  );
};
